import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Radio,
  Stack,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useAuth } from "../../../context/AuthContext";

// Igual que ajustar.php/guardarEmparejamiento.php: se elige UNA radio y se le
// relacionan una o más baterías. Solo actualiza equipo_bateria.relacion (+
// historial_bateria.relacion cuando la batería viene del historial de esta OT).
//
// Se dividen en dos emparejamientos independientes porque son dos situaciones
// distintas: equipos nuevos de esta OT (recién asignados) vs. equipos que ya
// estaban activos en el cliente (se les cambia de a qué radio están relacionados).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

// Un bloque de emparejamiento (radio + baterías) con su propia selección y guardado.
function BloqueEmparejamiento({ titulo, radios, baterias, onEmparejado }) {
  const [radioSeleccionado, setRadioSeleccionado] = useState("");
  const [bateriasSeleccionadas, setBateriasSeleccionadas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const toggleBateria = (id) => {
    setBateriasSeleccionadas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const emparejar = async () => {
    if (!radioSeleccionado || !bateriasSeleccionadas.length) return;
    setGuardando(true);
    try {
      for (const id of bateriasSeleccionadas) {
        const bateria = baterias.find((b) => b.id === id);
        if (!bateria) continue;
        await axios.put(`/api/equipos-bateria/${id}`, { relacion: radioSeleccionado });
        if (bateria.id_historial) {
          await axios.put(`/api/historial-bateria/${bateria.id_historial}`, { relacion: radioSeleccionado });
        }
      }
      setRadioSeleccionado("");
      setBateriasSeleccionadas([]);
      setOpenSnackbar(true);
      onEmparejado();
    } catch (err) {
      console.error("Error al emparejar equipos:", err);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {titulo}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!radioSeleccionado || !bateriasSeleccionadas.length || guardando}
          onClick={emparejar}
          sx={{ minHeight: 26, height: 26, fontSize: 11, whiteSpace: "nowrap" }}
        >
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Emparejar Seleccionadas"}
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Radio
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Serie</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!radios.length ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No hay radios en este grupo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  radios.map((r) => (
                    <TableRow
                      key={r.serie}
                      hover
                      selected={radioSeleccionado === r.serie}
                      onClick={() => setRadioSeleccionado(r.serie)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Radio size="small" checked={radioSeleccionado === r.serie} onChange={() => setRadioSeleccionado(r.serie)} sx={{ p: 0 }} />
                      </TableCell>
                      <TableCell>{r.serie}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ flex: 1.4, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Baterías
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Serie</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Relación actual</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!baterias.length ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No hay baterías en este grupo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  baterias.map((b) => {
                    const checked = bateriasSeleccionadas.includes(b.id);
                    return (
                      <TableRow key={b.id} hover selected={checked}>
                        <TableCell>
                          <Checkbox size="small" checked={checked} onChange={() => toggleBateria(b.id)} sx={{ p: 0 }} />
                        </TableCell>
                        <TableCell>{b.serie}</TableCell>
                        <TableCell>{b.modelo}</TableCell>
                        <TableCell>{b.relacion || "—"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Baterías emparejadas correctamente.
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}

// estadoNuevo/estadoActivo: literales de estado a filtrar en historial_radio/
// equipo_radio respectivamente. Por defecto son los de Adición/Reemplazo
// ("Asignado"/"Activo"); Venta los pasa distintos ("Asignado Vendido"/"Vendido
// Activo") porque su propio ciclo de vida usa otro vocabulario de estados.
// razonActiva (opcional): filtra el lado "Equipos del Cliente" de las BATERÍAS por
// equipo_bateria.razon — usado por la OT de Prueba ("prueba") para no mezclar
// baterías ya prestadas con el resto del equipo Activo del cliente. El lado Radio no
// se filtra (mismo criterio que el propio ajustar.php legacy de Prueba, que tampoco
// ofrece un pool de radios "a prueba" del cliente, solo el de esta OT).
const EmparejarEquiposOT = ({ cod_ot, cod_servicio_cliente, estadoNuevo = "Asignado", estadoActivo = "Activo", razonActiva }) => {
  const { user } = useAuth();
  const [radios, setRadios] = useState([]);
  const [baterias, setBaterias] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = () => {
    setLoading(true);
    // Solo equipo Activo de la sucursal del usuario logueado — mismo criterio
    // que DevolucionEquiposOT/SeleccionarMantenimientoOT. El equipo del
    // historial de esta OT (historial-radio/historial-bateria) no se filtra:
    // ya está asignado a esta OT, no es un pool para elegir.
    const ciudad = user?.sucursal || undefined;
    const paramsBateriaActiva = { estado: estadoActivo, cod_servicio: cod_servicio_cliente, ciudad };
    if (razonActiva) paramsBateriaActiva.razon = razonActiva;
    Promise.all([
      axios.get(`/api/historial-radio`, { params: { cod_ot } }),
      axios.get(`/api/equipos-radio`, { params: { estado: estadoActivo, cod_servicio: cod_servicio_cliente, ciudad } }),
      axios.get(`/api/historial-bateria`, { params: { cod_ot } }),
      axios.get(`/api/equipos-bateria`, { params: paramsBateriaActiva }),
    ])
      .then(([resRadioHist, resRadioActivos, resBateriaHist, resBateriaActivas]) => {
        const radiosNuevos = (resRadioHist.data || [])
          .filter((r) => r.estado === estadoNuevo)
          .map((r) => ({ serie: r.serie, origen: "Nueva" }));
        const radiosActivos = (resRadioActivos.data || []).map((r) => ({ serie: r.serie, origen: "Activa" }));
        const seriesVistas = new Set();
        const radiosCombinados = [...radiosNuevos, ...radiosActivos].filter((r) => {
          if (seriesVistas.has(r.serie)) return false;
          seriesVistas.add(r.serie);
          return true;
        });
        setRadios(radiosCombinados);

        const bateriasNuevas = (resBateriaHist.data || [])
          .filter((b) => b.estado === estadoNuevo)
          .map((b) => ({
            id: b.equipo_id,
            id_historial: b.id_historial,
            serie: b.serie,
            modelo: b.modelo,
            relacion: b.relacion_actual,
            origen: "Nueva",
          }));
        const bateriasActivas = (resBateriaActivas.data || []).map((b) => ({
          id: b.ID,
          id_historial: null,
          serie: b.serie,
          modelo: b.modelo,
          relacion: b.relacion,
          origen: "Activa",
        }));
        const idsVistos = new Set();
        const bateriasCombinadas = [...bateriasNuevas, ...bateriasActivas].filter((b) => {
          if (!b.id || idsVistos.has(b.id)) return false;
          idsVistos.add(b.id);
          return true;
        });
        setBaterias(bateriasCombinadas);
      })
      .catch((err) => console.error("Error al cargar radios/baterías para emparejar", err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot, cod_servicio_cliente, estadoNuevo, estadoActivo, razonActiva]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const radiosOT = radios.filter((r) => r.origen === "Nueva");
  const bateriasOT = baterias.filter((b) => b.origen === "Nueva");
  const radiosCliente = radios.filter((r) => r.origen === "Activa");
  const bateriasCliente = baterias.filter((b) => b.origen === "Activa");

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems="stretch">
        <BloqueEmparejamiento
          titulo="Equipos de la OT"
          radios={radiosOT}
          baterias={bateriasOT}
          onEmparejado={cargar}
        />
        <BloqueEmparejamiento
          titulo="Equipos del Cliente"
          radios={radiosCliente}
          baterias={bateriasCliente}
          onEmparejado={cargar}
        />
      </Stack>
    </Box>
  );
};

export default EmparejarEquiposOT;
